import React from 'react'

/* -------------------------------------------------------------------------
 * A TRIGGER CHILD THAT CANNOT HOLD A REF, AND WHY A SEAM THAT BINDS ONE WITH
 * `asChild` HAS TO NOTICE.
 *
 * WIRED INTO EXACTLY ONE SEAM TODAY: `src/antd/Dropdown` (see
 * `Dropdown.shadcn.tsx`). The other two `asChild` trigger bindings are
 * knowingly NOT covered — `src/antd/Tooltip.shadcn.tsx`, whose
 * `TooltipTrigger asChild` wraps only for the DISABLED and NON-ELEMENT cases
 * and not for this one, and `src/antd/Select/Select.shadcn.tsx`'s
 * `PopoverAnchor asChild`, which is unwrapped. Widening it to those two is
 * the follow-up `fe-overlay-seam-ref-capable-widening`, held back because a
 * wrapper around ~300 tooltip triggers and ~550 Select anchors is a
 * product-visible layout change on surfaces nobody has looked at. So this is
 * a helper the Dropdown seam uses, NOT an invariant the overlay seams all
 * hold.
 *
 * Radix binds a trigger with `asChild`: it clones the child and hands it a
 * ref. React silently drops a ref given to a PLAIN FUNCTION component — one
 * console warning in development, nothing in production — and the consequences
 * are entirely invisible from the markup, because every other prop still lands
 * on the DOM node the child renders:
 *
 *   • Radix has no anchor, so floating-ui measures the reference as a 0x0 box
 *     at the origin and places the panel from THERE. Measured on the Customers
 *     list before this guard existed: the "Action" menu opened with all four
 *     of its items, `data-state="open"`, `z-index: 1050` — at (0, -340), fully
 *     outside the viewport. Nothing appeared to happen.
 *   • `src/antd/Dropdown`'s `getPopupContainer` never sees a trigger node, so
 *     the popup falls back to the body instead of the container the call site
 *     asked for.
 *
 * The child that caused it, `src/new-components/filter-menu-button`, is now a
 * `forwardRef` — but it was not written wrong on purpose, and a plain function
 * component is the ordinary thing to write. So the SEAM carries the guard:
 * when the child cannot take a ref it gets a wrapper element that can, and the
 * seam anchors to that.
 *
 * THE WRAPPER IS THE LAST RESORT, NOT THE DEFAULT — the same warning
 * `./disabledTrigger.ts` carries. A wrapper is a real box: it, not the child,
 * becomes the flex/grid item, so any length the child sized against its parent
 * resolves against a shrink-to-fit box instead. `display: inline-block` with
 * no other geometry keeps that as small as it can be, and `canHoldRef` is
 * written to return TRUE for everything that can possibly work — host
 * elements, `forwardRef`, `memo`, class components, `lazy`, and
 * styled-components (which are `forwardRef` objects) — so the wrapper appears
 * only where the alternative is a broken overlay.
 * ------------------------------------------------------------------------- */

const FORWARD_REF = Symbol.for('react.forward_ref')
const MEMO = Symbol.for('react.memo')
const LAZY = Symbol.for('react.lazy')

type MaybeExotic = {
  $$typeof?: symbol
  type?: unknown
  prototype?: { isReactComponent?: unknown }
}

const typeCanHoldRef = (type: unknown, depth = 0): boolean => {
  if (depth > 5) return true
  // A host element ('button', 'span', …) always takes a ref.
  if (typeof type === 'string') return true
  if (!type) return false

  const exotic = type as MaybeExotic
  if (exotic.$$typeof === FORWARD_REF) return true
  if (exotic.$$typeof === LAZY) return true
  if (exotic.$$typeof === MEMO) return typeCanHoldRef(exotic.type, depth + 1)
  // A class component's instance is the ref.
  if (typeof type === 'function' && exotic.prototype?.isReactComponent)
    return true

  // Anything else callable is a plain function component: the ref is dropped.
  return typeof type !== 'function'
}

/** True when this trigger child can receive the ref Radix `asChild` gives it. */
export const canHoldRef = (node: React.ReactNode): boolean =>
  !React.isValidElement(node) || typeCanHoldRef(node.type)

/**
 * The element an overlay seam should bind `asChild` to: the child itself when
 * it can hold a ref, otherwise the smallest wrapper that can.
 *
 * `props` are the ones the seam needs ON the trigger (hover handlers today) —
 * they follow the ref onto whichever node ends up being the trigger.
 */
export const refCapableTrigger = (
  child: React.ReactElement,
  props: Record<string, unknown>
): React.ReactElement =>
  canHoldRef(child) ? (
    React.cloneElement(child, props as never)
  ) : (
    <span style={{ display: 'inline-block' }} {...props}>
      {child}
    </span>
  )
